// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Box, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type {
  Bundle,
  Patient,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import { Document, Loading, useMedplum, useMedplumProfile } from '@medplum/react';
import { questionnaireResponseToValues, withTenantScope } from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { botIdentifier } from '../lib/bot-identifier';
import { searchInsurancePayers } from '../lib/insurance-payers';
import { storeIntakeConsentSignature } from '../lib/intake-consent-signature';
import { SelfReferralForm, type SelfReferralFormValues } from './onboarding/SelfReferralForm';

function initialValues(patient: Patient): Partial<SelfReferralFormValues> {
  const name = patient.name?.[0];
  const email = patient.telecom?.find((contact) => contact.system === 'email')?.value ?? '';
  const phone = patient.telecom?.find((contact) => contact.system === 'phone')?.value ?? '';
  return {
    'first-name': name?.given?.[0] ?? '',
    'last-name': name?.family ?? '',
    email,
    phone,
    'date-of-birth': patient.birthDate ? dayjs(patient.birthDate).format('MM/DD/YYYY') : '',
    'street-address': patient.address?.[0]?.line?.[0] ?? '',
    'address-line-2': patient.address?.[0]?.line?.[1] ?? '',
    city: patient.address?.[0]?.city ?? '',
    state: patient.address?.[0]?.state ?? '',
    'zip-code': patient.address?.[0]?.postalCode ?? '',
  };
}

function draftAnswer(item: QuestionnaireItem, value: unknown): QuestionnaireResponseItem['answer'] {
  if (Array.isArray(value)) {
    return value.map((entry) => ({ valueString: String(entry) }));
  }
  if (item.type === 'boolean' && typeof value === 'boolean') {
    return [{ valueBoolean: value }];
  }
  if (item.type === 'date' && typeof value === 'string' && value) {
    return [{ valueDate: dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') }];
  }
  if (item.type === 'decimal' && typeof value === 'string' && value) {
    return [{ valueDecimal: Number(value) }];
  }
  if (typeof value === 'string' && value) {
    const coding = item.answerOption?.find(
      (option) => option.valueCoding?.code === value || option.valueString === value
    )?.valueCoding;
    return coding ? [{ valueCoding: coding }] : [{ valueString: value }];
  }
  return undefined;
}

function buildDraftResponse(
  questionnaire: Questionnaire,
  patient: Patient,
  values: SelfReferralFormValues
): QuestionnaireResponse {
  const items: QuestionnaireResponseItem[] = [];
  for (const item of questionnaire.item ?? []) {
    const answer = draftAnswer(item, values[item.linkId as keyof SelfReferralFormValues]);
    if (answer?.length) {
      items.push({ linkId: item.linkId, answer });
    }
  }
  return {
    resourceType: 'QuestionnaireResponse',
    questionnaire: `Questionnaire/${questionnaire.id ?? questionnaire.name}`,
    subject: { reference: `Patient/${patient.id}` },
    status: 'in-progress',
    authored: new Date().toISOString(),
    item: items,
  };
}

function valuesFromDraft(draft: QuestionnaireResponse): Partial<SelfReferralFormValues> {
  const values = questionnaireResponseToValues(draft);
  const result: Partial<SelfReferralFormValues> = {};
  for (const [key, value] of Object.entries(values)) {
    if (typeof value === 'string' || typeof value === 'boolean' || Array.isArray(value)) {
      (result as Record<string, unknown>)[key] = value;
    }
  }
  return result;
}

export function ExistingPatientIntakePage(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [prefilledValues, setPrefilledValues] = useState<Partial<SelfReferralFormValues>>({});
  const draftId = useRef<string | undefined>(undefined);
  const draftTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!profile) {
      return;
    }
    Promise.all([
      fetch('/public-questionnaires.json').then((response) => response.json() as Promise<Bundle>),
      medplum.searchOne('QuestionnaireResponse', {
        subject: `Patient/${profile.id}`,
        questionnaire: 'Questionnaire/self-referral-intake-form',
        status: 'in-progress',
      }),
    ])
      .then(([bundle, draft]) => {
        const resource = bundle.entry?.find(
          (entry) =>
            entry.resource?.resourceType === 'Questionnaire' && entry.resource.name === 'self-referral-intake-form'
        )?.resource;
        if (resource?.resourceType === 'Questionnaire') {
          setQuestionnaire(resource);
        }
        setPrefilledValues({ ...initialValues(profile), ...(draft ? valuesFromDraft(draft) : {}) });
        draftId.current = draft?.id;
      })
      .catch((loadError) => {
        Sentry.captureException(loadError);
        setError(normalizeErrorString(loadError));
      })
      .finally(() => setLoading(false));
  }, [medplum, profile]);

  if (loading) {
    return <Loading />;
  }
  if (!profile || !questionnaire) {
    return <Alert color="red">Unable to load the intake form. Please contact support.</Alert>;
  }

  const handleSubmit = async (
    response: QuestionnaireResponse,
    signature?: string,
    agreementPdfBase64?: string
  ): Promise<void> => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      const result = (await Sentry.startSpan(
        { op: 'bot.execute', name: 'Execute process-existing-patient-intake' },
        () =>
          medplum.executeBot(
            botIdentifier('process-existing-patient-intake'),
            {
              patientReference: createReference(profile),
              questionnaireResponse: response,
              organizationId: import.meta.env.VITE_MEDPLUM_ORGANIZATION_ID,
              consentSignature: signature,
              consentAgreementPdf: agreementPdfBase64,
            },
            'application/json'
          )
      )) as { eligible: boolean; uniqueId?: string; reason?: string };

      if (!result.eligible || !result.uniqueId) {
        setError(result.reason ?? 'We could not complete your intake. Please contact support.');
        return;
      }
      if (signature) {
        storeIntakeConsentSignature(result.uniqueId, signature, agreementPdfBase64);
      }
      window.location.href = `/onboarding/${result.uniqueId}`;
    } catch (submitError) {
      Sentry.captureException(submitError);
      showNotification({ color: 'red', message: normalizeErrorString(submitError), autoClose: false });
    } finally {
      setSubmitting(false);
    }
  };

  const handleValuesChange = (values: SelfReferralFormValues): void => {
    if (draftTimer.current) {
      clearTimeout(draftTimer.current);
    }
    draftTimer.current = setTimeout(() => {
      const draft = withTenantScope(
        buildDraftResponse(questionnaire, profile, values),
        import.meta.env.VITE_MEDPLUM_ORGANIZATION_ID
      );
      const save = draftId.current
        ? medplum.updateResource<QuestionnaireResponse>({ ...draft, id: draftId.current })
        : medplum.createResource<QuestionnaireResponse>(draft);
      save
        .then((saved) => {
          draftId.current = saved.id;
        })
        .catch((draftError) => {
          Sentry.captureException(draftError);
        });
    }, 500);
  };

  return (
    <>
      <Document width={800} bg="var(--mantine-color-white)">
        <Title order={2} mb="md" ta="center">
          Complete Your Intake
        </Title>
        <Text ta="center" c="dimmed">
          Review your information, complete consent and insurance details, then schedule your intake appointment.
        </Text>
      </Document>
      <Document width={800} bg="var(--mantine-color-white)" px="xl">
        {error && (
          <Alert color="red" mb="md">
            {error}
          </Alert>
        )}
        <Box px="xl">
          <SelfReferralForm
            questionnaire={questionnaire}
            initialValues={prefilledValues}
            onValuesChange={handleValuesChange}
            onInsuranceSearch={searchInsurancePayers}
            onSubmit={(response, signature, agreementPdf) => {
              handleSubmit(response, signature, agreementPdf).catch(console.error);
            }}
            submitting={submitting}
          />
        </Box>
      </Document>
    </>
  );
}
